import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TimerContext = createContext(null);

export function TimerProvider({ children }) {
  const { user } = useAuth();
  const [active, setActive] = useState(null); // {order_id, deliverable_id, title, type, address, time_seconds, timer_started_at}
  const [now, setNow] = useState(Date.now());

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/timer/active");
      setActive(data.active || null);
    } catch {
      /* not authed / offline — leave as is */
    }
  }, []);

  useEffect(() => {
    if (user) refresh();
    else setActive(null);
  }, [user, refresh]);

  // Tick every second while a timer is running so the display counts up live.
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);

  const start = useCallback(async (orderId, delivId) => {
    const { data } = await api.post(`/orders/${orderId}/deliverables/${delivId}/timer/start`);
    setActive(data.active || null);
    setNow(Date.now());
    return data.active;
  }, []);

  const stop = useCallback(async () => {
    if (!active) return null;
    const { data } = await api.post(`/orders/${active.order_id}/deliverables/${active.deliverable_id}/timer/stop`);
    setActive(null);
    return data;
  }, [active]);

  const elapsed = active
    ? (active.time_seconds || 0) +
      Math.max(0, Math.floor((now - new Date(active.timer_started_at).getTime()) / 1000))
    : 0;

  const value = useMemo(() => ({ active, elapsed, start, stop, refresh }), [active, elapsed, start, stop, refresh]);
  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}

export function useTimer() {
  return useContext(TimerContext);
}
